// Video conferencing integration using Jitsi Meet
import { toast } from './utils.js';

class VideoConference {
    // Static property to cache the Jitsi API loading promise
    static jitsiAPILoadingPromise = null;
    
    constructor() {
        this.domain = 'meet.jit.si';
        
        // Get adaptive video quality settings based on connection
        const videoQualitySettings = this.getAdaptiveVideoSettings();
        
        this.config = {
            disableDeepLinking: true,
            disableInviteFunctions: true,
            doNotStoreRoom: true,
            enableWelcomePage: false,
            prejoinPageEnabled: false,
            disableRemoteMute: true,
            remoteVideoMenu: {
                disableKick: true,
                disableGrantModerator: true
            },
            localRecording: {
                disable: true
            },
            p2p: {
                enabled: false
            },
            videoQuality: videoQualitySettings
        };
        
        this.interfaceConfig = {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            TOOLBAR_BUTTONS: [
                'microphone', 'camera', 'desktop', 'fullscreen',
                'fodeviceselection', 'hangup', 'chat', 'settings',
                'videoquality', 'tileview'
            ],
            SETTINGS_SECTIONS: ['devices', 'language', 'moderator'],
            DISPLAY_WELCOME_PAGE_CONTENT: false,
            DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT: false,
            GENERATE_ROOMNAMES_ON_WELCOME_PAGE: false,
            MOBILE_APP_PROMO: false,
            SHOW_CHROME_EXTENSION_BANNER: false
        };
    }
    
    // Get adaptive video settings based on connection type
    getAdaptiveVideoSettings() {
        // Check if Network Information API is available
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        
        let maxBitrates = {
            low: 150000,      // 150 kbps for low quality
            standard: 400000, // 400 kbps for standard
            high: 800000      // 800 kbps for high (reduced from 1.5 Mbps)
        };
        
        const preferredCodec = 'H264'; // Better compatibility than VP9
        let resolution = 720; // Default resolution
        
        if (connection) {
            const effectiveType = connection.effectiveType;
            const downlink = connection.downlink || 10; // Mbps
            
            // Adjust based on connection type
            switch (effectiveType) {
            case 'slow-2g':
            case '2g':
                maxBitrates = {
                    low: 50000,
                    standard: 80000,
                    high: 120000
                };
                resolution = 360;
                break;
            case '3g':
                maxBitrates = {
                    low: 100000,
                    standard: 200000,
                    high: 350000
                };
                resolution = 480;
                break;
            case '4g':
                // Check actual bandwidth
                if (downlink < 2) {
                    maxBitrates = {
                        low: 150000,
                        standard: 300000,
                        high: 500000
                    };
                    resolution = 480;
                } else if (downlink < 5) {
                    maxBitrates = {
                        low: 200000,
                        standard: 400000,
                        high: 700000
                    };
                    resolution = 720;
                }
                break;
            }
            
            // Log connection info for debugging
            console.log(`Connection type: ${effectiveType}, Downlink: ${downlink}Mbps`);
        }
        
        // Check if user is on mobile
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile) {
            // Further reduce bitrates for mobile devices
            maxBitrates.high = Math.min(maxBitrates.high, 500000);
            maxBitrates.standard = Math.min(maxBitrates.standard, 300000);
            maxBitrates.low = Math.min(maxBitrates.low, 150000);
        }
        
        return {
            preferredCodec: preferredCodec,
            maxBitratesVideo: maxBitrates,
            resolution: resolution,
            constraints: {
                video: {
                    height: {
                        ideal: resolution,
                        max: resolution
                    }
                }
            }
        };
    }
    
    // Generate secure room name from booking ID
    generateRoomName(bookingId) {
        // Add timestamp to ensure uniqueness
        const timestamp = Date.now();
        return `cycle-help-${bookingId}-${timestamp}`;
    }
    
    // Create meeting link
    createMeetingLink(bookingId) {
        const roomName = this.generateRoomName(bookingId);
        return `https://${this.domain}/${roomName}`;
    }
    
    // Initialize video conference in a container
    async initializeConference(containerId, bookingId, userInfo) {
        // Check if container exists
        const container = document.getElementById(containerId);
        if (!container) {
            throw new Error('Video container not found');
        }
        
        // Load Jitsi Meet API if not already loaded
        if (!window.JitsiMeetExternalAPI) {
            await this.loadJitsiAPI();
        }
        
        const roomName = this.generateRoomName(bookingId);
        
        // Initialize Jitsi Meet
        const api = new window.JitsiMeetExternalAPI(this.domain, {
            roomName: roomName,
            parentNode: container,
            width: '100%',
            height: '100%',
            configOverwrite: this.config,
            interfaceConfigOverwrite: this.interfaceConfig,
            userInfo: {
                displayName: userInfo.displayName,
                email: userInfo.email
            }
        });
        
        // Set up event listeners
        api.on('readyToClose', () => {
            container.innerHTML = '';
            this.onMeetingEnd(bookingId);
        });
        
        api.on('participantJoined', (participant) => {
            console.log('Participant joined:', participant);
        });
        
        api.on('participantLeft', (participant) => {
            console.log('Participant left:', participant);
        });
        
        api.on('videoConferenceJoined', () => {
            console.log('Conference joined');
            this.onMeetingStart(bookingId);
        });
        
        return api;
    }
    
    // Load Jitsi Meet API
    loadJitsiAPI() {
        // Return cached promise if already loading or loaded
        if (VideoConference.jitsiAPILoadingPromise) {
            return VideoConference.jitsiAPILoadingPromise;
        }
        
        // Create and cache the loading promise
        VideoConference.jitsiAPILoadingPromise = new Promise((resolve, reject) => {
            // Check if already loaded
            if (window.JitsiMeetExternalAPI) {
                resolve();
                return;
            }
            
            const script = document.createElement('script');
            script.src = 'https://meet.jit.si/external_api.js';
            script.async = true;
            
            script.onload = () => {
                console.log('Jitsi Meet API loaded successfully');
                resolve();
            };
            
            script.onerror = (error) => {
                console.error('Failed to load Jitsi Meet API:', error);
                // Reset the promise on error so it can be retried
                VideoConference.jitsiAPILoadingPromise = null;
                reject(error);
            };
            
            document.head.appendChild(script);
        });
        
        return VideoConference.jitsiAPILoadingPromise;
    }
    
    // Callback when meeting starts
    onMeetingStart(bookingId) {
        // Log meeting start time
        if (window.db) {
            window.db.collection('bookings').doc(bookingId).update({
                meetingStartedAt: firebase.firestore.FieldValue.serverTimestamp()
            }).catch(console.error);
        }
    }
    
    // Callback when meeting ends
    onMeetingEnd(bookingId) {
        // Log meeting end time
        if (window.db) {
            window.db.collection('bookings').doc(bookingId).update({
                meetingEndedAt: firebase.firestore.FieldValue.serverTimestamp()
            }).catch(console.error);
        }
        
        // Show feedback modal
        if (window.showFeedbackModal) {
            window.showFeedbackModal(bookingId);
        }
    }
    
    // Create a modal for video conference
    createVideoModal(bookingId, userInfo) {
        // Remove existing modal if any
        const existingModal = document.getElementById('videoConferenceModal');
        if (existingModal) {
            existingModal.remove();
        }
        
        // Create modal HTML
        const modal = document.createElement('div');
        modal.id = 'videoConferenceModal';
        modal.className = 'modal';
        modal.style.display = 'block';
        modal.innerHTML = `
            <div class="modal-content" style="max-width: 90%; width: 1200px; height: 90vh; padding: 0;">
                <div style="background: var(--dark); color: white; padding: 15px 20px; display: flex; justify-content: space-between; align-items: center;">
                    <h3 style="margin: 0;">Video Session</h3>
                    <button data-action="close-video-modal" 
                            style="background: none; border: none; color: white; font-size: 24px; cursor: pointer;">
                        &times;
                    </button>
                </div>
                <div id="jitsi-container" style="width: 100%; height: calc(100% - 60px);"></div>
            </div>
        `;
        
        document.body.appendChild(modal);
        
        // Add event listener for close button
        const closeBtn = modal.querySelector('[data-action="close-video-modal"]');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => modal.remove());
        }
        
        // Initialize conference
        this.initializeConference('jitsi-container', bookingId, userInfo)
            .catch(error => {
                console.error('Error initializing video conference:', error);
                toast.error('Error starting video conference. Please try again.');
                modal.remove();
            });
    }
    
    // Test connection before starting
    async testConnection() {
        try {
            const response = await fetch(`https://${this.domain}/config.js`);
            return response.ok;
        } catch (error) {
            console.error('Connection test failed:', error);
            return false;
        }
    }
}

// Create singleton instance
const videoConference = new VideoConference();

// Export for use in other modules
export default videoConference;